/**
 * EpisodeCard — one resident's concern episode: score, narrative, suggested
 * action, signal chips, sparkline, and lifecycle actions. Shared by the
 * dashboard widget and the resident-profile panel.
 *
 * Props:
 *   episode        — episode row with nested `signals`
 *   onAcknowledge  — (episodeId) => void
 *   onResolve      — (episodeId, actionTaken) => void
 *   onDismiss      — (episodeId, reason, muteDays) => void
 *   isProcessing   — boolean
 *   showResident   — show name/room in the header (dashboard true, profile false)
 *   readOnly       — hide lifecycle buttons (closed episodes)
 */

import { useState } from 'react';
import {
    TRIGGER_CONFIG, STATUS_CONFIG, DISMISS_REASONS, ACTION_OPTIONS, ACTION_LABELS,
    scoreTier, getSignalMetrics, getEpisodeWeeklyData, getResidentDisplayName, getResidentRoom,
} from './trendConfig.js';

const WeeklySparkline = ({ data, color }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data, 1);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 24 }}>
            {data.map((v, i) => (
                <div key={i} style={{ width: 6, height: `${Math.max((v / max) * 100, 4)}%`, background: i === data.length - 1 ? color : `${color}60`, borderRadius: 2 }} />
            ))}
        </div>
    );
};

const MetricPill = ({ label, value }) => (
    <div style={{ background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', padding: '6px 12px', display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</span>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>{value}</span>
    </div>
);

const MenuPopover = ({ title, options, onSelect, onClose }) => (
    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20, background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 230, padding: 4 }}>
        <div style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
        {options.map((opt) => (
            <div
                key={opt.value}
                onClick={() => { onClose(); onSelect(opt); }}
                style={{ padding: '6px 10px', fontSize: 'var(--font-size-xs)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-body)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-tertiary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
                {opt.label}
            </div>
        ))}
    </div>
);

const SignalChip = ({ signal }) => {
    const cfg = TRIGGER_CONFIG[signal.trigger_type] || {};
    const cleared = !!signal.cleared_at;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)', padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-tertiary)', color: cleared ? 'var(--color-text-muted)' : 'var(--color-text-body)', opacity: cleared ? 0.6 : 1 }} title={cleared ? 'No longer firing' : 'Currently firing'}>
            <span>{cfg.icon}</span>
            <span style={{ textDecoration: cleared ? 'line-through' : 'none' }}>{cfg.label || signal.trigger_type}</span>
        </span>
    );
};

const EpisodeCard = ({ episode, onAcknowledge, onResolve, onDismiss, isProcessing, showResident = true, readOnly = false }) => {
    const [expanded, setExpanded] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [dismissOpen, setDismissOpen] = useState(false);

    const tier = scoreTier(episode.score || 0);
    const status = STATUS_CONFIG[episode.status] || STATUS_CONFIG.new;
    const signals = episode.signals || [];
    const weeklyData = getEpisodeWeeklyData(episode);
    const isNew = !episode.status || episode.status === 'new';
    const suggested = ACTION_LABELS[episode.suggested_action];

    return (
        <div style={{ border: '1px solid var(--color-border)', borderLeft: `3px solid ${tier.color}`, borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-2)', overflow: 'visible', background: 'var(--color-bg-surface)' }}>
            <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '10px 14px', cursor: 'pointer' }}>
                {/* Score badge */}
                <span title={`Concern score: ${episode.score}`} style={{ minWidth: 34, textAlign: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '4px 0', borderRadius: 'var(--radius-md)', background: `${tier.color}18`, color: tier.color, flexShrink: 0 }}>
                    {episode.score ?? 0}
                </span>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        {showResident ? (
                            <>
                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>
                                    {getResidentDisplayName(episode)}
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{getResidentRoom(episode)}</span>
                            </>
                        ) : null}
                        {!isNew || readOnly ? (
                            <span style={{ fontSize: 10, fontWeight: 600, color: status.color, background: `${status.color}14`, padding: '1px 8px', borderRadius: 'var(--radius-full)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                {status.label}
                            </span>
                        ) : null}
                        {episode.occurrence_number > 1 ? (
                            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', background: 'var(--color-bg-tertiary)', padding: '1px 8px', borderRadius: 'var(--radius-full)' }}>
                                Occurrence #{episode.occurrence_number}
                            </span>
                        ) : null}
                        {signals.map((s) => <SignalChip key={s.signal_id} signal={s} />)}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 2, whiteSpace: expanded ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.45 }}>
                        {episode.narrative || ''}
                    </div>
                </div>

                {/* Sparkline */}
                {weeklyData.length > 0 ? <WeeklySparkline data={weeklyData} color={tier.color} /> : null}

                {/* Lifecycle actions */}
                {!readOnly ? (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                        {isNew ? (
                            <button disabled={isProcessing} onClick={() => onAcknowledge(episode.episode_id)} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }}>
                                {isProcessing ? '...' : 'Acknowledge'}
                            </button>
                        ) : (
                            <button disabled={isProcessing} onClick={() => { setResolveOpen(!resolveOpen); setDismissOpen(false); }} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }}>
                                {isProcessing ? '...' : 'Resolve'}
                            </button>
                        )}
                        <button disabled={isProcessing} onClick={() => { setDismissOpen(!dismissOpen); setResolveOpen(false); }} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px', color: 'var(--color-text-muted)' }}>
                            Dismiss
                        </button>
                        {resolveOpen ? (
                            <MenuPopover
                                title="Action taken"
                                options={ACTION_OPTIONS}
                                onClose={() => setResolveOpen(false)}
                                onSelect={(opt) => onResolve(episode.episode_id, opt.value)}
                            />
                        ) : null}
                        {dismissOpen ? (
                            <MenuPopover
                                title="Dismiss reason"
                                options={DISMISS_REASONS}
                                onClose={() => setDismissOpen(false)}
                                onSelect={(opt) => onDismiss(episode.episode_id, opt.value, opt.muteDays)}
                            />
                        ) : null}
                    </div>
                ) : null}

                {/* Expand arrow */}
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
            </div>

            {/* Suggested action strip */}
            {suggested && !readOnly ? (
                <div style={{ padding: '0 14px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Suggested</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)' }}>{suggested}</span>
                </div>
            ) : null}

            {/* Expanded evidence */}
            {expanded ? (
                <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--color-border)' }}>
                    {signals.map((signal) => {
                        const cfg = TRIGGER_CONFIG[signal.trigger_type] || {};
                        const metrics = getSignalMetrics(signal);
                        return (
                            <div key={signal.signal_id} style={{ marginTop: 'var(--space-3)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                    <span style={{ fontSize: 14 }}>{cfg.icon}</span>
                                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-heading)' }}>{cfg.label}</span>
                                    {signal.cleared_at ? (
                                        <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>no longer firing</span>
                                    ) : null}
                                </div>
                                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                    {metrics.map((m, i) => <MetricPill key={i} label={m.label} value={m.value} />)}
                                </div>
                                {signal.description ? (
                                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 6, lineHeight: 1.5 }}>{signal.description}</p>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

export default EpisodeCard;
